(() => {
  'use strict';
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-instagram-gallery]').forEach((gallery) => {
      if (gallery.dataset.instagramReady === 'true') return;
      gallery.dataset.instagramReady = 'true';
      const dialog = gallery.querySelector('[data-instagram-dialog]');
      const panel = gallery.querySelector('[data-instagram-panel]');
      const templates = [...gallery.querySelectorAll('template[data-instagram-post]')];
      const buttons = [...gallery.querySelectorAll('[data-instagram-open]')];
      let activeIndex = 0;
      let requestVersion = 0;
      let previousFocus = null;
      let feedPromise = null;
      let feedTimestamp = 0;
      let overrides = {};
      try { overrides = JSON.parse(gallery.querySelector('[data-instagram-video-overrides]')?.textContent || '{}'); } catch (_) { /* Keep local posts available. */ }

      const safeVideoURL = (value) => {
        if (typeof value !== 'string' || !value.trim()) return null;
        try {
          const baseURL = new URL(document.baseURI);
          const url = new URL(value, baseURL);
          return url.protocol === 'https:' || (url.protocol === 'http:' && url.origin === baseURL.origin) ? url.href : null;
        } catch (_) { return null; }
      };
      const fetchFeed = () => {
        if (feedPromise && Date.now() - feedTimestamp < 300000) return feedPromise;
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 8000);
        feedTimestamp = Date.now();
        feedPromise = fetch(gallery.dataset.feedEndpoint, {
          method: 'GET', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer',
          headers: { Accept: 'application/json' }, signal: controller.signal
        }).then((response) => {
          if (!response.ok) throw new Error('Unavailable feed');
          return response.json();
        }).then((feed) => {
          if (Number(feed?.meta?.code) !== 200 || !Array.isArray(feed.data)) throw new Error('Invalid feed');
          return feed.data;
        }).catch((error) => {
          feedPromise = null;
          throw error;
        }).finally(() => window.clearTimeout(timer));
        return feedPromise;
      };
      const stopVideo = () => {
        panel.querySelectorAll('video').forEach((video) => {
          video.pause();
          video.removeAttribute('src');
          video.load();
        });
      };
      const videoFailure = (message, retry = false) => {
        const status = panel.querySelector('[data-instagram-message]');
        const poster = panel.querySelector('[data-instagram-poster]');
        const video = panel.querySelector('[data-instagram-video]');
        const button = panel.querySelector('[data-instagram-play]');
        if (poster) poster.hidden = false;
        if (video) { video.pause(); video.hidden = true; }
        if (status) { status.textContent = message; status.hidden = false; }
        if (button) button.hidden = !retry;
      };
      const loadVideo = async (postID, version) => {
        const video = panel.querySelector('[data-instagram-video]');
        if (!video) return;
        const status = panel.querySelector('[data-instagram-message]');
        const retry = panel.querySelector('[data-instagram-play]');
        if (retry) retry.hidden = true;
        if (status) { status.textContent = 'Chargement de la vidéo…'; status.hidden = false; }
        try {
          let url = safeVideoURL(overrides[postID]);
          if (!url && gallery.dataset.sourceVideos === 'true') {
            const posts = await fetchFeed();
            const post = posts.find((candidate) => String(candidate.id) === postID);
            url = safeVideoURL(post?.videos?.standard_resolution?.url);
          }
          if (version !== requestVersion || !dialog.open) return;
          if (!url) {
            videoFailure('La vidéo de cette publication est momentanément indisponible.', gallery.dataset.sourceVideos === 'true');
            return;
          }
          video.addEventListener('error', () => {
            if (version !== requestVersion || !dialog.open) return;
            feedPromise = null;
            videoFailure('La vidéo de cette publication est momentanément indisponible.', true);
          }, { once: true });
          video.src = url;
          video.hidden = false;
          panel.querySelector('[data-instagram-poster]').hidden = true;
          if (status) status.hidden = true;
          video.load();
          const playback = video.play();
          if (playback?.catch) playback.catch(() => { /* Native controls remain available. */ });
        } catch (_) {
          if (version === requestVersion && dialog.open) videoFailure('La vidéo de cette publication est momentanément indisponible.', true);
        }
      };
      const showPost = (index) => {
        if (!templates.length) return;
        stopVideo();
        requestVersion += 1;
        activeIndex = (index + templates.length) % templates.length;
        const template = templates[activeIndex];
        panel.replaceChildren(template.content.cloneNode(true));
        panel.scrollTop = 0;
        if (template.dataset.postType === 'video') loadVideo(template.dataset.instagramPost, requestVersion);
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        previousFocus = button;
        if (!dialog.open) dialog.showModal();
        showPost(templates.findIndex((template) => template.dataset.instagramPost === button.dataset.instagramOpen));
      }));
      gallery.querySelector('[data-instagram-close]').addEventListener('click', () => dialog.close());
      gallery.querySelector('[data-instagram-previous]').addEventListener('click', () => showPost(activeIndex - 1));
      gallery.querySelector('[data-instagram-next]').addEventListener('click', () => showPost(activeIndex + 1));
      panel.addEventListener('click', (event) => {
        if (event.target.closest('[data-instagram-play]')) loadVideo(templates[activeIndex].dataset.instagramPost, ++requestVersion);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (['INPUT', 'TEXTAREA', 'VIDEO'].includes(event.target.tagName)) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); showPost(activeIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); showPost(activeIndex + 1); }
      });
      dialog.addEventListener('close', () => {
        requestVersion += 1;
        stopVideo();
        panel.replaceChildren();
        previousFocus?.focus({ preventScroll: true });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
